import { Draggable } from '@hello-pangea/dnd';
import {
  IconAlertTriangle, IconArrowRight, IconBag, IconClock, IconMoped, IconUtensils, IconWhatsapp,
} from '../atoms/Icon';
import { MODALITIES, paymentLabel } from '../api/ordersApi';
import { formatBRL } from '../utils/currency';
import { whatsappLink } from '../utils/whatsapp';
import { formatTableLabel } from '../utils/orderLabels';

/** Um glifo por modalidade: reconhecido antes da leitura, que é o que uma
 *  cozinha faz com o card. */
const MODALITY_ICONS = {
  delivery: IconMoped,
  takeaway: IconBag,
  dine_in: IconUtensils,
};

function getInitials(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

/** O detalhe que a cozinha precisa junto da modalidade: para onde vai. */
function modalityDetail(order) {
  if (order.modality === 'dine_in') return formatTableLabel(order.tableLabel);
  if (order.modality === 'delivery') return order.address;
  return null;
}

function OrderCard({ order, index, color, bg, onDetails, onAdvance, nextLabel, dragDisabled }) {
  const modality = MODALITIES.find((m) => m.value === order.modality);
  const ModalityIcon = MODALITY_ICONS[order.modality] || IconBag;
  const detail = modalityDetail(order);
  const totalItems = order.items.reduce((acc, i) => acc + i.quantity, 0);
  // Nulo em pedido de balcão, que costuma não ter telefone: sem link, o botão
  // não é desenhado, em vez de aparecer desativado e ocupar a linha à toa.
  const conversa = whatsappLink(order.customerPhone);
  return (
    <Draggable draggableId={order.id} index={index} isDragDisabled={dragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={snapshot.isDragging ? '' : 'card-elevated'}
          style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: '14px 14px 12px',
            marginBottom: 10,
            boxShadow: snapshot.isDragging ? '0 12px 28px rgba(83,74,183,0.24)' : undefined,
            border: snapshot.isDragging
              ? `1.5px solid ${color}`
              : '1.5px solid transparent',
            borderTopWidth: 3,
            borderTopColor: color,
            cursor: dragDisabled ? 'default' : 'grab',
            transform: snapshot.isDragging ? 'rotate(-1deg) scale(1.02)' : undefined,
            transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.15s',
            ...provided.draggableProps.style,
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span
              className="fw-semibold"
              style={{
                fontSize: 15,
                color,
                backgroundColor: bg,
                borderRadius: 20,
                padding: '3px 11px',
              }}
            >
              #{order.number}
            </span>
            <span className="d-flex align-items-center gap-1" style={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
              <IconClock size={13} />
              {formatTime(order.createdAt)}
            </span>
          </div>

          {/* Tipo de pedido logo abaixo do número: é o que decide o que a
              equipe faz quando o pedido fica pronto, então vem antes dos itens.
              O detalhe (mesa ou endereço) anda na mesma linha — a modalidade
              sozinha não diz para onde vai.

              Sem pílula: o número do pedido logo acima já é uma, e duas em
              sequência viravam ruído repetido. Aqui o ícone carrega o
              reconhecimento e a cor faz o resto, sem pedir um fundo. */}
          <div className="order-modality">
            {/* O ícone ganha um quadro próprio — o mesmo recurso que a sidebar
                usa no item ativo. Solto, ele era só um risco fino do tamanho da
                letra; emoldurado, vira o ponto de entrada da linha sem precisar
                da pílula que estava ali antes. */}
            <span className="order-modality-icon" style={{ backgroundColor: bg, color }}>
              <ModalityIcon size={15} strokeWidth="2.1" />
            </span>
            <span style={{ color }}>{modality?.label || order.modality}</span>
            {detail && (
              <>
                <span className="order-modality-sep">·</span>
                <span className="order-modality-detail" title={detail}>{detail}</span>
              </>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 mb-2 pb-2" style={{ borderBottom: '1px solid #f2f2f2' }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                backgroundColor: bg,
                color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {getInitials(order.customerName)}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-neutral)' }}>
              {order.customerName}
            </div>
          </div>

          <ul className="mb-2 ps-0" style={{ listStyle: 'none', margin: 0 }}>
            {order.items.map((item) => (
              <li key={item.id} className="d-flex align-items-start gap-2" style={{ padding: '3px 0' }}>
                <span
                  className="fw-semibold text-center"
                  style={{
                    fontSize: 13,
                    color,
                    backgroundColor: bg,
                    borderRadius: 6,
                    minWidth: 26,
                    padding: '1px 4px',
                    flexShrink: 0,
                  }}
                >
                  {item.quantity}×
                </span>
                {/* Miniatura só quando existe: um placeholder cinza por linha
                    encheria o card sem informar nada. */}
                {item.imageUrl && (
                  <img src={item.imageUrl} alt="" className="order-item-thumb" />
                )}
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral)' }}>
                    {item.label}
                  </span>
                  {/* A cozinha precisa ver os adicionais junto do item: eles
                      mudam o preparo, não só o preço. */}
                  {item.addons?.length > 0 && (
                    <div className="order-item-addons">
                      {item.addons.map((a) => `+ ${a.name}`).join(' · ')}
                    </div>
                  )}
                </span>
              </li>
            ))}
          </ul>

          {order.notes && (
            <div
              className="d-flex align-items-start gap-2 mb-2"
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--color-neutral)',
                backgroundColor: bg,
                border: `1px solid ${color}55`,
                borderRadius: 8,
                padding: '7px 9px',
              }}
            >
              <IconAlertTriangle size={15} style={{ color, flexShrink: 0, marginTop: 1 }} />
              <span>{order.notes}</span>
            </div>
          )}

          <div
            className="d-flex justify-content-between align-items-center mb-3 pt-2"
            style={{ borderTop: '1px dashed #ddd' }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 500, color: '#666' }}>
              {totalItems} {totalItems === 1 ? 'item' : 'itens'}
            </span>
            {/* A forma de pagamento anda junto do total, e não junto da
                modalidade: as duas respondem à mesma pergunta do lojista —
                quanto e como eu recebo.

                Só quando existe. Pedido de balcão nasce sem forma registrada, e
                um "Não informado" em todo card de quem opera no balcão seria
                ruído em cima da informação que importa. O detalhe do pedido
                mostra o campo sempre, inclusive vazio. */}
            <span className="text-end">
              <span className="fw-semibold d-block" style={{ fontSize: 15, color: 'var(--color-neutral)' }}>
                {formatBRL(Number(order.totalAmount))}
              </span>
              {order.paymentMethod && (
                <span style={{ fontSize: 11.5, fontWeight: 500, color: '#888' }}>
                  {paymentLabel(order.paymentMethod)}
                </span>
              )}
            </span>
          </div>

          <div className="d-flex gap-2">
            {/* Atalho para a conversa do cliente — é por onde o comprovante do
                Pix chega. Sem ele, conferir um pagamento é ler o telefone no
                card, trocar de janela e procurar o contato à mão, uma vez por
                pedido. Só o ícone: a linha ainda precisa caber "Detalhes" e o
                botão de avançar, e o balão verde se reconhece sem rótulo.

                Âncora, e não botão: assim o lojista pode abrir em outra aba com
                o meio do mouse, que é como se acompanha vários pedidos ao mesmo
                tempo sem perder o quadro de vista. */}
            {conversa && (
              <a
                href={conversa}
                target="_blank"
                rel="noopener noreferrer"
                // O quadro é uma área de arrasto; sem isto, soltar o clique
                // sobre o link é interpretado como o fim de um arrasto do card.
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="d-flex align-items-center justify-content-center"
                title={`Abrir conversa com ${order.customerName} no WhatsApp`}
                aria-label={`Abrir conversa com ${order.customerName} no WhatsApp`}
                style={{
                  flex: '0 0 auto',
                  borderRadius: 8,
                  border: '1.5px solid #cdeedd',
                  backgroundColor: '#e9f9f0',
                  color: '#128c7e',
                  padding: '8px 10px',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#d8f3e6'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#e9f9f0'; }}
              >
                <IconWhatsapp size={16} />
              </a>
            )}

            <button
              onClick={onDetails}
              className="d-flex align-items-center justify-content-center gap-1"
              style={{
                flex: nextLabel ? '0 0 auto' : '1 1 auto',
                borderRadius: 8,
                border: '1.5px solid #ddd',
                backgroundColor: '#fff',
                color: '#555',
                fontSize: 12.5,
                fontWeight: 500,
                padding: '8px 10px',
                cursor: 'pointer',
                transition: 'background-color 0.15s, border-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f7f7f7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
            >
              Detalhes
            </button>

            {nextLabel && (
              <button
                onClick={onAdvance}
                className="d-flex align-items-center justify-content-center gap-1"
                style={{
                  flex: '1 1 auto',
                  borderRadius: 8,
                  border: 'none',
                  backgroundColor: color,
                  color: '#fff',
                  fontSize: 12.5,
                  fontWeight: 600,
                  padding: '8px 10px',
                  cursor: 'pointer',
                  transition: 'filter 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.93)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
                title={`Mover para ${nextLabel}`}
              >
                {nextLabel}
                <IconArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default OrderCard;
